<template>
  <div id="contract-list" class="s-base">
    <v-card class="s-panel" max-width="1500" min-width="700">
      <v-card-title>
        <div class="s-title">契約者一覧画面</div>
        <v-btn class="ml-auto" color="secondary" @click="clickNew()" width="100"
          >新規</v-btn
        >
      </v-card-title>
      <v-container fluid>
        <v-row>
          <v-col>
            <v-data-table
              :headers="tableHeaders"
              :items="users"
              :hide-default-header="true"
              class="contract-list-table"
              :mobile-breakpoint="0"
            >
              <template v-slot:header>
                <tr>
                  <th class="white--text text-h5 pl-4" rowspan="3" width="15%">
                    契約者
                  </th>
                  <th
                    class="white--text text-h5 px-0 text-center"
                    rowspan="3"
                    width="15%"
                  >
                    ＩＤ
                  </th>
                  <th
                    class="white--text text-h6 text-center"
                    style="border-bottom: 1px solid #ffffff"
                    :colspan="dataTypeList.length * deliveryTypeList.length"
                  >
                    契約内容
                  </th>
                </tr>
                <tr>
                  <th
                    class="white--text text-subtitile-1 px-0 text-center px-1"
                    style="border-bottom: 1px solid #ffffff"
                    :colspan="deliveryTypeList.length"
                    v-for="dataType in dataTypeList"
                    :key="dataType.code"
                  >
                    {{ dataType.name }}
                  </th>
                </tr>
                <tr class="px-1">
                  <template v-for="dataType in dataTypeList">
                    <th
                      class="white--text text-body-2 px-0 text-center"
                      v-for="deliv in deliveryTypeList"
                      :key="dataType.code + '_' + deliv.code"
                      :style="{ minWidth: '40px' }"
                    >
                      {{ deliv.name }}
                    </th>
                  </template>
                </tr>
              </template>
              <template v-slot:item="{ item }">
                <tr @click="clickUser(item.userId)">
                  <td>{{ item.userName }}</td>
                  <td class="text-center">{{ item.userId }}</td>
                  <template v-for="dataType in dataTypeList">
                    <td
                      v-for="deliv in deliveryTypeList"
                      :key="dataType.code + '_' + deliv.code"
                      class="text-center"
                    >
                      <span v-if="hasContract(item, dataType.code, deliv.code)"
                        >〇</span
                      >
                    </td>
                  </template>
                </tr>
              </template>
            </v-data-table>
          </v-col>
        </v-row>
        <v-btn class="mt-3" color="primary" @click="clickExit()" width="100"
          >戻る</v-btn
        >
      </v-container>
    </v-card>
    <request-error-dialog
      :requestErrorDialogFlag.sync="dialog.requestError"
      :httpStatus="httpStatus"
      message=""
    />
  </div>
</template>

<script lang="ts">
import { userApi, User } from "../api/UserApi";
import { dataTypeApi, DataType } from "../api/DataTypeApi";
import { deliveryTypeApi, DeliveryType } from "../api/DeliveryTypeApi";
import { Component, Vue } from "vue-property-decorator";
import RequestErrorDialog from "./RequestErrorDialog.vue";
import { NETWORK_ERROR } from "../router";
import Axios from "axios";

@Component({
  components: {
    "request-error-dialog": RequestErrorDialog,
  },
})
export default class ContractList extends Vue {
  users: User[] = [];
  dataTypeList: DataType[] = [];
  deliveryTypeList: DeliveryType[] = [];

  /** v-data-table用のヘッダープロパティ */
  tableHeaders = [
    {
      text: "契約者",
      value: "userName",
      width: "18%",
    },
    { text: "ID", value: "userId", width: "10%" },
    { text: "契約内容", value: "contracts", align: "center" },
  ];

  dialog = {
    requestError: false,
  };
  httpStatus = 500;

  clickNew() {
    this.$router.push({ name: "ContractDetail" });
  }

  clickUser(userId: string) {
    this.$router.push({ name: "ContractDetail", params: { userId } });
  }

  clickExit() {
    this.$router.push({ name: "Menu" });
  }

  hasContract(user: User, dataTypeCode: number, delivCd: number): boolean {
    return (
      user.contracts?.some(
        (it) => it.dataType === dataTypeCode && it.delivCd === delivCd
      ) ?? false
    );
  }

  async mounted() {
    try {
      const result = {
        user: await userApi.getUserList(),
        dataType: await dataTypeApi.getDataTypeList(),
        deliveryType: await deliveryTypeApi.getDeliveryTypeList(),
      };
      this.users = result.user.userListResponse.result;
      this.dataTypeList = result.dataType.dataTypeResponse.result.dataTypeList;
      this.deliveryTypeList =
        result.deliveryType.deliveryTypeResponse.result.deliveryTypeList;
    } catch (error) {
      this.users = [];
      let newError: Error;
      if (Axios.isAxiosError(error) && error.response) {
        newError = new Error(error.response.status.toString());
      } else {
        newError = new Error(NETWORK_ERROR);
      }
      if (newError.message === "500") {
        // 500 Internal Server Error
        this.httpStatus = 500;
        this.dialog.requestError = true;
      } else if (newError.message === "401") {
        // 401 Unauthorized
        this.httpStatus = 401;
        this.dialog.requestError = true;
      } else {
        // 運用上起こってはいけないエラー
        this.$router.push({ name: newError.message });
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/scss/common.scss";

.contract-list-table {
  width: 100%;
  border-collapse: collapse;
  text-align: left;
  table-layout: fixed;
  ::v-deep .v-data-table__wrapper {
    overflow-x: hidden !important;
  }
  ::v-deep td {
    white-space: normal;
    overflow: anywhere;
    word-break: break-all;
    text-overflow: clip;
  }
  ::v-deep th {
    padding-top: 5px;
    padding-bottom: 5px;
    padding-left: 5px;
    border: none;
    background-color: #2b72de;
    span {
      font-size: 17px;
      font-weight: 400;
      color: #ffffff;
    }
    .v-icon {
      font-size: 20px !important;
      color: #d4d4d4 !important;
    }
    white-space: normal;
    overflow: anywhere;
    word-break: break-all;
    text-overflow: clip;
  }
  ::v-deep tr:nth-child(even) {
    background-color: #f0ffff;
  }
}
</style>
